import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UiDialogComponent } from '@template-ui/components';
import { DocMeta } from '@template-ui/decorators';
import { UiButtonDirective } from '@template-ui/directives';
import { EzDialogData } from '@template-ui/types';

/** Library-owned samples cannot import the application's showcase-name constants. */
const SHOWCASE_LIBRARY_NAMES = { BASE: 'Base Library' } as const;

/** Small dialog repro; avoids the base component's unrelated application service dependencies. */
@DocMeta({
  title: 'Dialog',
  hide: true,
  category: 'Components',
  showcaseTitle: 'Safe primary action',
  showcaseDesc: 'Shared footer action appearance and initial focus, with Escape and backdrop dismissal preserving content.',
  showcaseAttachTo: [{ libName: SHOWCASE_LIBRARY_NAMES.BASE, classNames: ['EzDialogComponent'] }],
})
@Component({
  selector: 'ui-dialog-safe-action-sample',
  imports: [UiButtonDirective],
  templateUrl: './ui-dialog-safe-action-sample.component.html',
  styleUrl: './ui-dialog-safe-action-sample.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiDialogSafeActionSampleComponent {
  /** Announces the last sample result without changing persisted content. */
  readonly result = signal('No action taken.');

  /** Uses the existing overlay and focus restoration behavior. */
  private readonly dialog = inject(MatDialog);

  /** Opens a reversible demonstration of a safe default action. */
  open(): void {
    this.dialog.open(UiDialogComponent, {
      autoFocus: 'first-tabbable',
      restoreFocus: true,
      ariaLabel: 'Remove sample content?',
      data: {
        heading: 'Remove sample content?',
        content: 'This sample only records your choice.',
        buttons: [
          { text: 'Keep content', severity: 'primary', outlined: false, defaultFocus: true, action: ref => ref.close(false) },
          { text: 'Remove content', severity: 'danger', outlined: true, action: ref => ref.close(true) },
        ],
      } satisfies EzDialogData,
    }).afterClosed().subscribe(removed => this.result.set(removed === true ? 'Removal selected.' : 'Content kept.'));
  }
}
